// @flow
import './index.less';

import {useTranslation} from 'gatsby-plugin-react-i18next';
import React, {useMemo} from 'react';
import Markdown from 'react-markdown';

import {round} from '_utils/math';

type Props = {
  plan: Object,
  promotionCode?: ?Object,
  showDisclaimer?: boolean,
  onClick?: () => void,
};

const PlanItem = ({plan, promotionCode, showDisclaimer = false, onClick}: Props): React$Node => {
  const {t} = useTranslation();

  const discountPercentage = useMemo(() => {
    if (!promotionCode) return 0;

    return promotionCode.coupon.percentOff;
  }, [promotionCode]);

  const name = useMemo(() => t(plan.metadata.name), [t, plan]);
  const months = useMemo(() => {
    switch (plan.metadata.planType) {
      case 'annual':
        return 12;

      case 'quarterly':
        return 3;

      case 'monthly':
      default:
        return 1;
    }
  }, [plan]);
  const origTotalPrice = useMemo(() => plan.unitAmount / 100, [plan]);
  const totalPrice = useMemo(
    () => (discountPercentage > 0 ? (origTotalPrice * (100 - discountPercentage)) / 100 : origTotalPrice),
    [origTotalPrice, discountPercentage],
  );
  const unitPrice = useMemo(() => round(totalPrice / months, 2), [totalPrice, months]);
  const freeTrialDays = useMemo(
    () => promotionCode?.metadata?.trialPeriodDays || plan.recurring.trialPeriodDays,
    [promotionCode, plan],
  );
  const currency = useMemo(() => plan.currency.toUpperCase(), [plan]);
  const campaign = useMemo(() => promotionCode?.metadata?.campaign, [promotionCode]);

  console.log({campaign});

  const tagsElem = useMemo(() => {
    const {savings} = plan.metadata;

    const tags = [];
    if (discountPercentage > 0) {
      tags.push(t('Common_PercentOff', {percent: discountPercentage}));
    } else if (savings && savings !== '0') {
      tags.push(t('Common_Saving', {savings}));
    }

    if (tags.length === 0) return null;

    return (
      <div className="tags">
        {tags.map((tag, idx) => (
          <div key={`tag-${idx}`} className="tag">
            {tag}
          </div>
        ))}
      </div>
    );
  }, [t, plan, discountPercentage]);

  const description = useMemo(() => {
    const lines = [];
    if (freeTrialDays > 0) {
      lines.push(t('CheckoutSection_IncludeNDayFreeTrial', {freeTrialDays}));
      // George: temporary hack, remove "else if condition" after RTM campaign is ended
    } else if (campaign === 'rtm') {
      lines.push('');
    } else {
      lines.push(t('CheckoutSection_StartsImmediately'));
    }

    if (showDisclaimer) {
      if (freeTrialDays > 0) {
        lines.push(
          t('CheckoutSection_Disclaimer', {
            currency,
            origTotalPrice: round(origTotalPrice, 2),
          }),
        );
        // George: temporary hack, remove "else if condition" after RTM campaign is ended
      } else if (campaign === 'rtm') {
        lines.push('You will be billed after your 30 day free access ends');
      } else {
        lines.push(
          t('CheckoutSection_Disclaimer_NoTrial', {
            currency,
            origTotalPrice: round(origTotalPrice, 2),
          }),
        );
      }
    }
    return lines.join('\n\n');
  }, [t, freeTrialDays, showDisclaimer, origTotalPrice, currency, campaign]);

  return (
    <div className="plan-item" onClick={onClick} onKeyPress={null} role="button" tabIndex="0">
      <div className="content-container">
        <div className="name">
          <span>{name}</span>
          {tagsElem}
        </div>
        <Markdown className="description">{description}</Markdown>
      </div>
      <div className="price-container">
        <div className="price">
          {origTotalPrice !== totalPrice && (
            <span className="orig-price">{`${currency} ${round(origTotalPrice, 2)}`}</span>
          )}
          {`${currency} ${round(totalPrice, 2)}`}
        </div>
        {months > 1 && <div className="unit-price">{`${currency} ${round(unitPrice, 2)}/mo`}</div>}
      </div>
    </div>
  );
};

export default PlanItem;
